<script>
import { at2x } from "@/common/responsive-helpers";
import { mediaWatchEventListenerPonyfill } from "@/polyfills";

export default {
  name: "VideoResponsive",
  props: {
    src: {
      type: String,
      required: true
    },
    width: {
      type: String,
      default: undefined
    },
    height: {
      type: String,
      default: undefined
    },
    play: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    loop: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    controls: {
      type: Boolean,
      default: false
    },
    muted: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    playsinline: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    videoClass: {
      type: [String, Array, Object],
      default: undefined
    },
    labelledBy: {
      type: String,
      default: undefined
    }
  },
  emits: ["ended"],
  data() {
    return {
      autoplay: this.play,
      reducedMotionMediaQuery: null,
      isReducedMotion: false,
      isHighDpi: false
    };
  },
  computed: {
    responsiveSrc() {
      return this.isHighDpi ? at2x(this.src) : this.src;
    },
    type() {
      const ext = this.src.split(".").pop();

      return `video/${ext}`;
    }
  },
  watch: {
    responsiveSrc() {
      if (this.$refs.video) {
        this.$refs.video.load();
      }
    },
    play(value) {
      if (!this.$refs.video) {
        return;
      }

      if (value) {
        this.$refs.video.play();
      } else {
        this.$refs.video.pause();
      }
    }
  },
  mounted() {
    this.reducedMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.prefersReducedMotionChange(this.reducedMotionMediaQuery);
    mediaWatchEventListenerPonyfill.addEventListener(
      this.reducedMotionMediaQuery,
      this.prefersReducedMotionChange
    );

    // We assume device pixel ratio won't change on the device
    // So we don't have a change listener here
    const highDpiMediaQuery = window.matchMedia(
      "(-webkit-min-device-pixel-ratio: 2), (min--moz-device-pixel-ratio: 2), (-o-min-device-pixel-ratio: 2/1), (min-device-pixel-ratio: 2), (min-resolution: 192dpi), (min-resolution: 2dppx)"
    );
    this.highDpiChange(highDpiMediaQuery);
  },
  beforeUnmount() {
    mediaWatchEventListenerPonyfill.removeEventListener(
      this.reducedMotionMediaQuery,
      this.prefersReducedMotionChange
    );
  },
  methods: {
    prefersReducedMotionChange(mq) {
      this.isReducedMotion = mq.matches === true;
    },
    highDpiChange(mq) {
      this.isHighDpi = mq.matches === true;
    }
  }
};
</script>

<template>
  <div>
    <slot v-if="isReducedMotion" name="reducedMotion"></slot>
    <video
      v-if="!isReducedMotion"
      ref="video"
      class="image-responsive"
      :class="videoClass"
      :autoplay="autoplay"
      :loop="loop"
      :controls="controls"
      :muted="muted"
      :playsinline="playsinline"
      :width="width"
      :height="height"
      :aria-labelledby="labelledBy"
      @ended="$emit('ended')"
    >
      <source :src="responsiveSrc" :type="type" />
      <slot name="alternativeSources"></slot>
    </video>
  </div>
</template>
